import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, AlertTriangle, CheckCircle2, FileClock } from "lucide-react";

const TONE = {
  expired: "bg-red-50 text-red-700 border-red-200",
  expiring: "bg-amber-50 text-amber-700 border-amber-200",
  valid: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const deadlineText = (r) =>
  r.days_left < 0 ? `Lapsed ${Math.abs(r.days_left)} days ago`
    : r.days_left === 0 ? "Expires today"
    : `${r.days_left} days left`;

const Group = ({ title, rows, tone, testid, note }) => (
  <div className="surface p-6" data-testid={testid}>
    <div className="flex items-center justify-between gap-3">
      <p className="label-tiny">{title}</p>
      <span className="text-xs font-mono text-muted-foreground">{rows.length}</span>
    </div>
    {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
    {rows.length === 0 ? (
      <p className="mt-5 text-sm text-muted-foreground">Nothing here.</p>
    ) : (
      <div className="mt-4 divide-y divide-border">
        {rows.map((r) => (
          <div key={r.document_id} className="py-3 flex flex-wrap items-start justify-between gap-3"
            data-testid={`renewal-row-${r.document_id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold">
                {r.doc_type}
                <span className="font-normal text-muted-foreground"> · {r.title}</span>
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                <Link to={`/parcels/${r.parcel_id}`} className="text-primary hover:underline">
                  {r.parcel_name}
                </Link>
                {r.district ? ` · ${r.district}` : ""}
                {r.reference_no ? ` · Ref ${r.reference_no}` : ""}
              </p>
              {r.renewal_note && (
                <p className="mt-1 text-xs text-muted-foreground italic">{r.renewal_note}</p>
              )}
            </div>
            <div className="text-right shrink-0">
              <span className={`rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${tone}`}>
                {deadlineText(r)}
              </span>
              <p className="mt-1.5 text-xs font-mono text-muted-foreground">
                Valid till {fmtDate(r.valid_until)}
              </p>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function Renewals() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState("90");

  useEffect(() => {
    api.get("/renewals", { params: { days: Number(days) } })
      .then(({ data }) => setData(data)).catch(() => setData(false));
  }, [days]);

  if (data === null) return <div className="p-8 text-sm text-muted-foreground">Loading renewals…</div>;
  if (data === false) return <div className="p-8 text-sm text-destructive">Could not load renewals.</div>;

  const urgent = data.expired.length + data.expiring.length;

  return (
    <div className="p-5 md:p-10 max-w-[1200px] rise" data-testid="renewals-page">
      <p className="label-tiny">Validity tracking</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter flex items-center gap-3">
        <CalendarClock size={27} className="text-primary" /> Document renewals
      </h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
        Leases, property tax receipts, encumbrance certificates, insurance policies and NOCs with a
        validity date are tracked here. REAM emails admins every morning about anything that has
        lapsed or is inside its reminder window.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[200px]" data-testid="renewals-horizon-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Next 30 days</SelectItem>
            <SelectItem value="90">Next 90 days</SelectItem>
            <SelectItem value="180">Next 6 months</SelectItem>
            <SelectItem value="365">Next year</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground" data-testid="renewals-tracked-total">
          {data.tracked_total} document(s) have a validity date on file
        </span>
      </div>

      {urgent > 0 ? (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3"
          data-testid="renewals-alert">
          <AlertTriangle size={19} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">
              {urgent} document(s) need renewing
            </p>
            <p className="mt-1 text-xs text-amber-800">
              {data.expired.length} already lapsed · {data.expiring.length} inside the reminder window
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3"
          data-testid="renewals-clear">
          <CheckCircle2 size={19} className="text-emerald-600 shrink-0 mt-0.5" />
          <p className="text-sm font-semibold text-emerald-900">
            Nothing is lapsing in this window.
          </p>
        </div>
      )}

      <div className="mt-6 space-y-4">
        <Group title="Already lapsed" rows={data.expired} tone={TONE.expired}
          testid="renewals-expired" note="Renew and upload the fresh copy against the property." />
        <Group title="Expiring soon" rows={data.expiring} tone={TONE.expiring}
          testid="renewals-expiring" note="Inside the reminder window set on each document." />
        <Group title="Coming up later" rows={data.later} tone={TONE.valid}
          testid="renewals-later" />
      </div>

      <p className="mt-8 flex items-start gap-2 text-xs text-muted-foreground">
        <FileClock size={14} className="mt-0.5 shrink-0" />
        Add a validity date while uploading a document, or set it later from the property's Documents
        tab.
      </p>
    </div>
  );
}
